import { SEARCH_PARAM_KEYS } from '../../../constants/jobOrderConstants';
import { getTimeZone } from '../../../utils';
import parseQueryParamToSafeInteger from '../../../utils/queryParamParsers';

const parseStringToBoolean = (value) => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
};

const getUpcomingJobsDefaultFilters = (nowMs) => [
  { field: 'end', operation: 'after', value: nowMs },
];

const FILTER_FIELDS_TO_OPERATION = {
  corporation: { field: 'corporation', operation: 'equalsID' },
  associatedUser: { field: 'isAssociated', operation: 'equalsID' },
  fillRate: { field: 'full', operation: 'equals' },
  status: { field: 'cancelled', operation: 'equals' },
  shift: { field: 'shift', operation: 'betweenClockTimes' },
  position: { field: 'name', operation: 'icontains' },
  startDate: { field: 'start', operation: 'onOrAfter' },
  endDate: { field: 'end', operation: 'onOrBefore' },
};

/**
 * Builds API `filters[]` from URL query (single source of truth), with implicit defaults:
 * - status missing => Active => cancelled=false filter included
 * - startDate/endDate missing => Upcoming default filter included
 */
export default function buildApiFiltersFromSearchParams(searchParams, { nowMs = Date.now() } = {}) {
  const filters = [];

  // Corporation
  const corporationId = searchParams.get(SEARCH_PARAM_KEYS.CORPORATION);
  if (corporationId) {
    filters.push({
      field: FILTER_FIELDS_TO_OPERATION.corporation.field,
      operation: FILTER_FIELDS_TO_OPERATION.corporation.operation,
      value: corporationId,
    });
  }

  // Associated user (admin or recruiter)
  const associatedUserId = searchParams.get(SEARCH_PARAM_KEYS.ASSOCIATED_USER);
  if (associatedUserId) {
    filters.push({
      field: FILTER_FIELDS_TO_OPERATION.associatedUser.field,
      operation: FILTER_FIELDS_TO_OPERATION.associatedUser.operation,
      value: associatedUserId,
    });
  }

  // Fill rate
  const fillRate = parseStringToBoolean(searchParams.get(SEARCH_PARAM_KEYS.FILL_RATE));
  if (fillRate !== null) {
    filters.push({
      field: FILTER_FIELDS_TO_OPERATION.fillRate.field,
      operation: FILTER_FIELDS_TO_OPERATION.fillRate.operation,
      value: fillRate,
    });
  }

  // Status (implicit default = Active/false)
  const statusParam = searchParams.get(SEARCH_PARAM_KEYS.STATUS);
  const cancelled = parseStringToBoolean(statusParam);
  filters.push({
    field: FILTER_FIELDS_TO_OPERATION.status.field,
    operation: FILTER_FIELDS_TO_OPERATION.status.operation,
    value: cancelled === null ? false : cancelled,
  });

  // Position
  const position = searchParams.get(SEARCH_PARAM_KEYS.POSITION);
  if (position) {
    filters.push({
      field: FILTER_FIELDS_TO_OPERATION.position.field,
      operation: FILTER_FIELDS_TO_OPERATION.position.operation,
      value: position,
    });
  }

  // Shifts
  const shiftStart = searchParams.get(SEARCH_PARAM_KEYS.SHIFT_START);
  const shiftEnd = searchParams.get(SEARCH_PARAM_KEYS.SHIFT_END);
  const shiftStartInt = parseQueryParamToSafeInteger(shiftStart);
  const shiftEndInt = parseQueryParamToSafeInteger(shiftEnd);
  if (shiftStartInt !== null && shiftEndInt !== null) {
    filters.push({
      field: FILTER_FIELDS_TO_OPERATION.shift.field,
      operation: FILTER_FIELDS_TO_OPERATION.shift.operation,
      value: { start: shiftStartInt, end: shiftEndInt, zone: getTimeZone() },
    });
  }

  // Date range (explicit) OR default (upcoming)
  const startDate = searchParams.get(SEARCH_PARAM_KEYS.START_DATE);
  const endDate = searchParams.get(SEARCH_PARAM_KEYS.END_DATE);
  const startDateInt = parseQueryParamToSafeInteger(startDate);
  const endDateInt = parseQueryParamToSafeInteger(endDate);
  if (startDateInt !== null && endDateInt !== null) {
    filters.push({
      field: FILTER_FIELDS_TO_OPERATION.startDate.field,
      operation: FILTER_FIELDS_TO_OPERATION.startDate.operation,
      value: startDateInt,
    });
    filters.push({
      field: FILTER_FIELDS_TO_OPERATION.endDate.field,
      operation: FILTER_FIELDS_TO_OPERATION.endDate.operation,
      value: endDateInt,
    });
  } else {
    // Important: keep "now" stable unless caller decides otherwise.
    // If we used Date.now() on every render, we'd generate a different filter value and cause refetch loops later.
    filters.push(...getUpcomingJobsDefaultFilters(nowMs));
  }

  return filters;
}
